import React, { useState } from 'react';
import { Box, Grid, Card, CardContent, Typography, Button, TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Chip, FormControl, InputLabel, Select, MenuItem, Divider, LinearProgress, IconButton } from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import AssessmentIcon from '@mui/icons-material/Assessment';
import PaidIcon from '@mui/icons-material/Paid';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useApp } from '../context/AppContext';
import { useLanguage } from '../context/LanguageContext';

export default function FranchiseHQView() {
    const { menuItems, updateMenuPrice, showToast, inventory } = useApp();
    const { currentLanguage } = useLanguage();

    // Mock branches data
    const [branches, setBranches] = useState([
        { id: 'BR-01', name: 'Pune Express (पुणे कॅफे)', location: 'Deccan Gymkhana, Pune', sales: 48500, orders: 320, activeKots: 8, royaltyPaid: 1940, royaltyPending: 485 },
        { id: 'BR-02', name: 'Mumbai Highway (मुंबई हायवे)', location: 'Thane Highway, Mumbai', sales: 74200, orders: 495, activeKots: 14, royaltyPaid: 2968, royaltyPending: 742 },
        { id: 'BR-03', name: 'Kolhapur Bypass (कोल्हापूर बायपास)', location: 'Kolhapur NH-4 Bypass', sales: 39600, orders: 240, activeKots: 5, royaltyPaid: 1584, royaltyPending: 396 }
    ]);

    // Active selected branch for details
    const [selectedBranchId, setSelectedBranchId] = useState('all');

    // Menu editing state
    const [selectedMenuBranch, setSelectedMenuBranch] = useState('all');
    const [editingPrices, setEditingPrices] = useState({});

    // Inventory Variance Audit mock data
    const [varianceData, setVarianceData] = useState([
        { id: 'ing_chicken', name: 'Raw Chicken', theoretical: 45.2, actual: 42.0, unit: 'kg', price: 180 },
        { id: 'ing_paneer', name: 'Paneer', theoretical: 24.5, actual: 21.2, unit: 'kg', price: 320 },
        { id: 'ing_tomato', name: 'Tomatoes', theoretical: 58.0, actual: 51.5, unit: 'kg', price: 40 },
        { id: 'ing_onion', name: 'Onions', theoretical: 65.0, actual: 64.2, unit: 'kg', price: 30 },
        { id: 'ing_oil', name: 'Refined Oil', theoretical: 30.0, actual: 24.8, unit: 'Ltr', price: 140 }
    ]);

    // Handle price changes
    const handlePriceChange = (itemId, val) => {
        setEditingPrices(prev => ({
            ...prev,
            [itemId]: parseFloat(val) || 0
        }));
    };

    // Save menu price to branch
    const savePriceUpdate = (item) => {
        const newPrice = editingPrices[item.id] !== undefined ? editingPrices[item.id] : item.price;
        updateMenuPrice(item.id, newPrice);
        showToast(`Menu Price for ${item.name} pushed successfully to ${selectedMenuBranch === 'all' ? 'All Branches' : 'Selected Branch'}!`);
    };

    // Auto calculate totals
    const totalSales = branches.reduce((sum, b) => selectedBranchId === 'all' || b.id === selectedBranchId ? sum + b.sales : sum, 0);
    const totalOrders = branches.reduce((sum, b) => selectedBranchId === 'all' || b.id === selectedBranchId ? sum + b.orders : sum, 0);
    const totalActiveKots = branches.reduce((sum, b) => selectedBranchId === 'all' || b.id === selectedBranchId ? sum + b.activeKots : sum, 0);
    const totalRoyaltyDue = branches.reduce((sum, b) => selectedBranchId === 'all' || b.id === selectedBranchId ? sum + b.royaltyPending : sum, 0);

    const labels = {
        en: {
            title: 'Franchise HQ Central Control',
            branchSelect: 'Filter Branch View',
            allBranches: 'All Outlets',
            totalSales: 'Aggregate Chain Sales',
            totalOrders: 'Total Orders',
            activeKots: 'Live Kitchen Orders',
            royaltyDue: 'Royalty Receivable (5%)',
            tabPerformance: 'Branch Performance Matrix',
            tabMenu: 'Remote Menu Price Updater',
            tabInventory: 'Material Variance Audit (Anti-Theft)',
            thBranch: 'Outlet Name',
            thLocation: 'Location',
            thSales: 'Gross Sales (₹)',
            thOrders: 'Orders Count',
            thRoyalty: 'Royalty (Pending / Paid)',
            thStatus: 'Status',
            active: 'Active',
            menuItemName: 'Dish Name',
            currentPrice: 'Current Price',
            targetPrice: 'HQ Target Price (₹)',
            action: 'Action',
            pushUpdate: 'Push Price to Outlets',
            varianceTitle: 'Ingredient Leakage Report',
            varianceDesc: 'Compares theoretical recipe usage with actual physical stock audits to reveal theft or waste.',
            thIng: 'Ingredient',
            thTheo: 'Recipe Usage (Expected)',
            thAct: 'Audited Stock (Actual)',
            thVar: 'Variance',
            thCost: 'Wastage Cost (₹)',
            refesh: 'Recalculate Audit Data'
        },
        mr: {
            title: 'फ्रेंचायझी मुख्यालय केंद्र (HQ)',
            branchSelect: 'ब्रांच फिल्टर करा',
            allBranches: 'सर्व आऊटलेट्स',
            totalSales: 'एकूण चेन विक्री',
            totalOrders: 'एकूण ऑर्डर्स संख्या',
            activeKots: 'लाइव्ह किचन ऑर्डर्स',
            royaltyDue: 'एकूण येणे रॉयल्टी (५%)',
            tabPerformance: 'ब्रांच कामगिरी तक्ता',
            tabMenu: 'रिमोट मेनू किंमत नियंत्रण',
            tabInventory: 'साहित्य गळती ऑडिट (चोरी प्रतिबंधक)',
            thBranch: 'ब्रांचचे नाव',
            thLocation: 'पत्ता',
            thSales: 'एकूण विक्री (₹)',
            thOrders: 'ऑर्डर्स संख्या',
            thRoyalty: 'रॉयल्टी (बाकी / जमा)',
            thStatus: 'स्थिती',
            active: 'सुरू आहे',
            menuItemName: 'पदार्थाचे नाव',
            currentPrice: 'चालू किंमत',
            targetPrice: 'नवीन ठरवलेली किंमत (₹)',
            action: 'कृती',
            pushUpdate: 'किंमत अपडेट करा',
            varianceTitle: 'साहित्य गळती अहवाल (Leakage Report)',
            varianceDesc: 'रेसिपी नुसार लागणारे अपेक्षित साहित्य आणि प्रत्यक्षात शिल्लक साहित्य यातील तफावत (चोरी किंवा नासाडी) दर्शवते.',
            thIng: 'साहित्य',
            thTheo: 'अपेक्षित वापर (Expected)',
            thAct: 'प्रत्यक्ष शिल्लक (Actual)',
            thVar: 'फरक (Variance)',
            thCost: 'तफावत खर्च (₹)',
            refesh: 'पुन्हा ऑडिट करा'
        }
    };

    const l = labels[currentLanguage] || labels['en'];

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f8fafc', minHeight: 'calc(100vh - 70px)' }}>
            
            {/* Header controls */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <BusinessIcon sx={{ fontSize: 40, color: '#008751' }} />
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 900, color: '#1e293b' }}>
                            {l.title}
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 800 }}>
                            Centralized Management & Operations Portal
                        </Typography>
                    </Box>
                </Box>

                <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>{l.branchSelect}</InputLabel>
                    <Select
                        value={selectedBranchId}
                        label={l.branchSelect}
                        onChange={(e) => setSelectedBranchId(e.target.value)}
                        sx={{ bgcolor: '#ffffff', borderRadius: 2 }}
                    >
                        <MenuItem value="all">🌐 {l.allBranches}</MenuItem>
                        {branches.map(b => (
                            <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {/* Aggregated KPI Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ borderRadius: 3, borderLeft: '6px solid #008751' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', justify: 'space-between', alignItems: 'center' }}>
                                <Typography variant="subtitle2" sx={{ color: '#64748b', fontWeight: 800 }}>{l.totalSales}</Typography>
                                <TrendingUpIcon sx={{ color: '#008751' }} />
                            </Box>
                            <Typography variant="h4" sx={{ fontWeight: 900, color: '#1e293b', mt: 1.5 }}>
                                ₹{totalSales.toLocaleString()}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ borderRadius: 3, borderLeft: '6px solid #06b6d4' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', justify: 'space-between', alignItems: 'center' }}>
                                <Typography variant="subtitle2" sx={{ color: '#64748b', fontWeight: 800 }}>{l.totalOrders}</Typography>
                                <AssessmentIcon sx={{ color: '#06b6d4' }} />
                            </Box>
                            <Typography variant="h4" sx={{ fontWeight: 900, color: '#1e293b', mt: 1.5 }}>
                                {totalOrders}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ borderRadius: 3, borderLeft: '6px solid #f59e0b' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', justify: 'space-between', alignItems: 'center' }}>
                                <Typography variant="subtitle2" sx={{ color: '#64748b', fontWeight: 800 }}>{l.activeKots}</Typography>
                                <RestaurantMenuIcon sx={{ color: '#f59e0b' }} />
                            </Box>
                            <Typography variant="h4" sx={{ fontWeight: 900, color: '#1e293b', mt: 1.5 }}>
                                {totalActiveKots}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ borderRadius: 3, borderLeft: '6px solid #10b981' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', justify: 'space-between', alignItems: 'center' }}>
                                <Typography variant="subtitle2" sx={{ color: '#64748b', fontWeight: 800 }}>{l.royaltyDue}</Typography>
                                <PaidIcon sx={{ color: '#10b981' }} />
                            </Box>
                            <Typography variant="h4" sx={{ fontWeight: 900, color: '#1e293b', mt: 1.5 }}>
                                ₹{totalRoyaltyDue.toLocaleString()}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Core Sections Grid */}
            <Grid container spacing={4}>
                
                {/* 1. Branch Performance Matrix Table */}
                <Grid item xs={12}>
                    <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0' }}>
                        <Box sx={{ p: 3, borderBottom: '1px solid #e2e8f0', bgcolor: '#ffffff' }}>
                            <Typography variant="h6" sx={{ fontWeight: 900, color: '#1e293b' }}>
                                📊 {l.tabPerformance}
                            </Typography>
                        </Box>
                        <CardContent sx={{ p: 0 }}>
                            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 0 }}>
                                <Table>
                                    <TableHead sx={{ bgcolor: '#f8fafc' }}>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 900 }}>{l.thBranch}</TableCell>
                                            <TableCell sx={{ fontWeight: 900 }}>{l.thLocation}</TableCell>
                                            <TableCell sx={{ fontWeight: 900 }} align="right">{l.thSales}</TableCell>
                                            <TableCell sx={{ fontWeight: 900 }} align="center">{l.thOrders}</TableCell>
                                            <TableCell sx={{ fontWeight: 900 }} align="center">{l.thRoyalty}</TableCell>
                                            <TableCell sx={{ fontWeight: 900 }} align="center">{l.thStatus}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {branches.filter(b => selectedBranchId === 'all' || b.id === selectedBranchId).map(b => (
                                            <TableRow key={b.id} hover>
                                                <TableCell sx={{ fontWeight: 800 }}>{b.name}</TableCell>
                                                <TableCell sx={{ color: '#64748b', fontWeight: 600 }}>{b.location}</TableCell>
                                                <TableCell sx={{ fontWeight: 900, color: '#008751' }} align="right">₹{b.sales.toLocaleString()}</TableCell>
                                                <TableCell sx={{ fontWeight: 800 }} align="center">{b.orders}</TableCell>
                                                <TableCell align="center">
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                        <Chip label={`Paid: ₹${b.royaltyPaid}`} size="small" color="success" sx={{ mb: 0.5, fontWeight: 700 }} />
                                                        <Chip label={`Due: ₹${b.royaltyPending}`} size="small" color="warning" sx={{ fontWeight: 700 }} />
                                                    </Box>
                                                </TableCell>
                                                <TableCell align="center">
                                                    <Chip label={l.active} color="primary" size="small" icon={<CheckCircleIcon />} sx={{ fontWeight: 800 }} />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                </Grid>

                {/* 2. Remote Menu Pricing Controller */}
                <Grid item xs={12} lg={6}>
                    <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', height: '100%' }}>
                        <Box sx={{ p: 3, borderBottom: '1px solid #e2e8f0', bgcolor: '#ffffff', display: 'flex', justify: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 900, color: '#1e293b' }}>
                                🛠️ {l.tabMenu}
                            </Typography>
                            
                            <FormControl size="small" sx={{ minWidth: 160 }}>
                                <InputLabel>Target Branch</InputLabel>
                                <Select
                                    value={selectedMenuBranch}
                                    label="Target Branch"
                                    onChange={(e) => setSelectedMenuBranch(e.target.value)}
                                >
                                    <MenuItem value="all">🌐 All Branches</MenuItem>
                                    {branches.map(b => (
                                        <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        <CardContent sx={{ p: 0, maxHeight: 420, overflowY: 'auto' }}>
                            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 0 }}>
                                <Table size="small">
                                    <TableHead sx={{ bgcolor: '#f8fafc' }}>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 900, py: 1.5 }}>{l.menuItemName}</TableCell>
                                            <TableCell sx={{ fontWeight: 900, py: 1.5 }} align="center">{l.currentPrice}</TableCell>
                                            <TableCell sx={{ fontWeight: 900, py: 1.5 }} align="center">{l.targetPrice}</TableCell>
                                            <TableCell sx={{ fontWeight: 900, py: 1.5 }} align="center">{l.action}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {menuItems.map(item => {
                                            const nameKey = currentLanguage === 'mr' ? item.nameMr : (currentLanguage === 'hi' ? item.nameHi : item.name);
                                            const editingPrice = editingPrices[item.id] !== undefined ? editingPrices[item.id] : item.price;
                                            
                                            return (
                                                <TableRow key={item.id} hover>
                                                    <TableCell sx={{ fontWeight: 800 }}>{nameKey}</TableCell>
                                                    <TableCell sx={{ fontWeight: 800 }} align="center">₹{item.price}</TableCell>
                                                    <TableCell align="center">
                                                        <TextField
                                                            size="small"
                                                            type="number"
                                                            value={editingPrice}
                                                            onChange={(e) => handlePriceChange(item.id, e.target.value)}
                                                            sx={{ 
                                                                width: 70, 
                                                                '& input': { py: 0.5, px: 1, fontSize: '0.75rem', fontWeight: 800, textAlign: 'center' } 
                                                            }}
                                                        />
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <IconButton 
                                                            color="primary" 
                                                            onClick={() => savePriceUpdate(item)}
                                                            sx={{ bgcolor: '#00875115', '&:hover': { bgcolor: '#00875125' } }}
                                                        >
                                                            <SaveIcon sx={{ fontSize: 16, color: '#008751' }} />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                </Grid>

                {/* 3. Consumption Variance Auditor (Anti-theft) */}
                <Grid item xs={12} lg={6}>
                    <Card sx={{ borderRadius: 3, border: '1px solid #e2e8f0', height: '100%' }}>
                        <Box sx={{ p: 3, borderBottom: '1px solid #e2e8f0', bgcolor: '#ffffff', display: 'flex', justify: 'space-between', alignItems: 'center' }}>
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 900, color: '#1e293b' }}>
                                    🛡️ {l.varianceTitle}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, display: 'block', mt: 0.5 }}>
                                    {l.varianceDesc}
                                </Typography>
                            </Box>
                            
                            <IconButton 
                                onClick={() => showToast('Audit data recalculated successfully!')}
                                sx={{ border: '1px solid #e2e8f0', bgcolor: '#ffffff' }}
                            >
                                <RefreshIcon />
                            </IconButton>
                        </Box>

                        <CardContent sx={{ p: 0 }}>
                            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 0 }}>
                                <Table size="small">
                                    <TableHead sx={{ bgcolor: '#f8fafc' }}>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 900, py: 1.5 }}>{l.thIng}</TableCell>
                                            <TableCell sx={{ fontWeight: 900, py: 1.5 }} align="right">{l.thTheo}</TableCell>
                                            <TableCell sx={{ fontWeight: 900, py: 1.5 }} align="right">{l.thAct}</TableCell>
                                            <TableCell sx={{ fontWeight: 900, py: 1.5 }} align="center">{l.thVar}</TableCell>
                                            <TableCell sx={{ fontWeight: 900, py: 1.5 }} align="right">{l.thCost}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {varianceData.map(v => {
                                            const variance = v.actual - v.theoretical;
                                            const isNegative = variance < 0;
                                            const costLoss = Math.abs(variance * v.price);

                                            return (
                                                <TableRow key={v.id} hover>
                                                    <TableCell sx={{ fontWeight: 800 }}>{v.name}</TableCell>
                                                    <TableCell sx={{ fontWeight: 700, color: '#64748b' }} align="right">{v.theoretical} {v.unit}</TableCell>
                                                    <TableCell sx={{ fontWeight: 800 }} align="right">{v.actual} {v.unit}</TableCell>
                                                    <TableCell align="center">
                                                        <Chip 
                                                            label={`${variance > 0 ? '+' : ''}${variance.toFixed(1)} ${v.unit}`}
                                                            size="small"
                                                            color={isNegative ? 'error' : 'success'}
                                                            icon={isNegative ? <WarningAmberIcon sx={{ fontSize: 10 }} /> : undefined}
                                                            sx={{ fontWeight: 800, fontSize: '0.7rem' }}
                                                        />
                                                    </TableCell>
                                                    <TableCell sx={{ fontWeight: 900, color: isNegative ? 'error.main' : 'success.main' }} align="right">
                                                        {isNegative ? `₹${costLoss.toFixed(0)} (Loss)` : '₹0'}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </CardContent>
                    </Card>
                </Grid>

            </Grid>

        </Box>
    );
}
